import { isValidPortalTemplateId } from '../theme/loginPortalThemes';
import { getSharedPageAtmosphere, setSharedPageAtmosphere } from './sharedPageAtmosphere';

const LAYOUT_KEY = 'erp4c_public_portal_layout';
/** List/preview "panel" style (job cards, split columns) — independent of page background */
const CONTENT_TEMPLATE_KEY = 'erp4c_public_portal_content_template';
/** Optional override; when absent, split browse column uses main content template */
const SPLIT_LEFT_TEMPLATE_KEY = 'erp4c_public_portal_split_left_template';
/** Optional override; when absent, split preview column uses main content template */
const SPLIT_RIGHT_TEMPLATE_KEY = 'erp4c_public_portal_split_right_template';
const SPLIT_RATIO_KEY = 'erp4c_public_portal_split_ratio';
const SPLIT_LIST_HIDDEN_KEY = 'erp4c_public_portal_split_list_hidden';

/** Left column width as a fraction of the split container (0.18–0.48). Default favors the preview pane. */
export function getStoredSplitRatio() {
  try {
    const v = parseFloat(localStorage.getItem(SPLIT_RATIO_KEY));
    if (Number.isFinite(v) && v >= 0.18 && v <= 0.48) return v;
  } catch {
    /* ignore */
  }
  return 0.28;
}

export function setStoredSplitRatio(r) {
  try {
    const clamped = Math.min(0.48, Math.max(0.18, r));
    localStorage.setItem(SPLIT_RATIO_KEY, String(clamped));
  } catch {
    /* ignore */
  }
}

export function getStoredSplitListHidden() {
  try {
    return localStorage.getItem(SPLIT_LIST_HIDDEN_KEY) === '1';
  } catch {
    return false;
  }
}

export function setStoredSplitListHidden(hidden) {
  try {
    localStorage.setItem(SPLIT_LIST_HIDDEN_KEY, hidden ? '1' : '0');
  } catch {
    /* ignore */
  }
}

const TRAINING_LAYOUT_FORMAT_KEY = 'erp4c_public_portal_training_layout_format';

/** @typedef {'list' | 'box'} TrainingLayoutFormat */

/** @returns {TrainingLayoutFormat} */
export function getStoredTrainingLayoutFormat() {
  try {
    const v = localStorage.getItem(TRAINING_LAYOUT_FORMAT_KEY);
    if (v === 'list' || v === 'box') return v;
  } catch {
    /* ignore */
  }
  return 'list';
}

/** @param {TrainingLayoutFormat} format */
export function setStoredTrainingLayoutFormat(format) {
  try {
    if (format === 'list' || format === 'box') {
      localStorage.setItem(TRAINING_LAYOUT_FORMAT_KEY, format);
    }
  } catch {
    /* ignore */
  }
}

/** @typedef {'regular' | 'split'} PublicPortalLayout */
/** @typedef {import('../theme/loginPortalThemes').PublicPortalTemplateId} PublicPortalTemplate */

export function getStoredLayout() {
  try {
    const v = localStorage.getItem(LAYOUT_KEY);
    if (v === 'split') return 'split';
    return 'regular';
  } catch {
    return 'regular';
  }
}

export function setStoredLayout(/** @type {PublicPortalLayout} */ layout) {
  try {
    localStorage.setItem(LAYOUT_KEY, layout === 'regular' ? 'regular' : 'split');
  } catch {
    /* ignore */
  }
}

export function getStoredTemplate() {
  return getSharedPageAtmosphere();
}

export function setStoredTemplate(/** @type {PublicPortalTemplate} */ t) {
  setSharedPageAtmosphere(t);
}

export function getStoredContentTemplate() {
  try {
    const v = localStorage.getItem(CONTENT_TEMPLATE_KEY);
    if (isValidPortalTemplateId(v)) return v;
  } catch {
    /* ignore */
  }
  return getStoredTemplate();
}

export function setStoredContentTemplate(/** @type {PublicPortalTemplate} */ t) {
  try {
    const v = isValidPortalTemplateId(t) ? t : 'light';
    localStorage.setItem(CONTENT_TEMPLATE_KEY, v);
  } catch {
    /* ignore */
  }
}

/** @returns {PublicPortalTemplate | null} */
export function getStoredSplitLeftTemplateOverride() {
  try {
    const v = localStorage.getItem(SPLIT_LEFT_TEMPLATE_KEY);
    return isValidPortalTemplateId(v) ? v : null;
  } catch {
    return null;
  }
}

export function setStoredSplitLeftTemplateOverride(/** @type {PublicPortalTemplate} */ t) {
  try {
    const v = isValidPortalTemplateId(t) ? t : 'light';
    localStorage.setItem(SPLIT_LEFT_TEMPLATE_KEY, v);
  } catch {
    /* ignore */
  }
}

export function clearStoredSplitLeftTemplateOverride() {
  try {
    localStorage.removeItem(SPLIT_LEFT_TEMPLATE_KEY);
  } catch {
    /* ignore */
  }
}

/** @returns {PublicPortalTemplate | null} */
export function getStoredSplitRightTemplateOverride() {
  try {
    const v = localStorage.getItem(SPLIT_RIGHT_TEMPLATE_KEY);
    return isValidPortalTemplateId(v) ? v : null;
  } catch {
    return null;
  }
}

export function setStoredSplitRightTemplateOverride(/** @type {PublicPortalTemplate} */ t) {
  try {
    const v = isValidPortalTemplateId(t) ? t : 'light';
    localStorage.setItem(SPLIT_RIGHT_TEMPLATE_KEY, v);
  } catch {
    /* ignore */
  }
}

export function clearStoredSplitRightTemplateOverride() {
  try {
    localStorage.removeItem(SPLIT_RIGHT_TEMPLATE_KEY);
  } catch {
    /* ignore */
  }
}
